import { Alert } from "@mui/material";
import { BodyTextM, cs, useIsDesktop } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { type FC } from "react";
import { usePrimaryAPIClient } from "../../context/PrimaryAPIClient";
import { useQueryKeyFactory } from "../../context/QueryKeyProvider";
import { useHasSPError } from "../../context/SPErrorContext";
import { useIsUserLoggedIn } from "../../hooks/useIsUserLoggedIn";
import { BannerWrapperDesktop, BannerWrapperMobile } from "../Layout";
import { PensionBannerWrapper } from "./PensionBannerWrapper";
import { PensionDpsMigrationBannerWrapper } from "./PensionDpsMigrationBannerWrapper";
import { PensionNovelBannerWrapper } from "./PensionNovelBannerWrapper";

const BannerComponent = ({
    severity,
    value,
}: {
    severity: "error" | "warning" | "info" | "success";
    value: string;
}): JSX.Element => (
    <Alert
        severity={severity}
        sx={{
            zIndex: 999,
            pr: 4,
            pl: 4,
        }}
    >
        <BodyTextM dangerouslySetInnerHTML={{ __html: value }} />
    </Alert>
);

type InfoBannersProps = {
    showCoreSystemDownWarning: boolean;
};

const InfoBanners = ({ showCoreSystemDownWarning }: InfoBannersProps): JSX.Element | null => {
    const { hasError: isSpError } = useHasSPError();
    const isDesktop = useIsDesktop();
    const { bannersApi } = usePrimaryAPIClient();
    const isLoggedUser = useIsUserLoggedIn();
    const { createQueryKey } = useQueryKeyFactory();

    const { data } = useQuery({
        queryKey: createQueryKey({
            key: ["bannersApi.getCmsWarningsGet"],
            includeUserIdentifier: false,
        }),
        queryFn: () => bannersApi.getCmsWarningsGet(),
        enabled: isLoggedUser,
    });

    const WrapperComponent = isDesktop ? BannerWrapperDesktop : BannerWrapperMobile;

    if (!isLoggedUser) return null;

    return (
        <WrapperComponent>
            {data
                ? Object.entries(data).map(([key, value]: [string, unknown]) => {
                      if (!value || value === "") return null;

                      if (data.error && !isEmpty(data.error) && key !== "error") return null;

                      const severityKey = key as "error" | "warning" | "info" | "success";
                      const severityValue = String(value);

                      return <BannerComponent key={key} severity={severityKey} value={severityValue} />;
                  })
                : null}

            {isSpError && showCoreSystemDownWarning && !data?.error ? (
                <BannerComponent severity="error" value={cs.global.starPensionUnavailableWarning} />
            ) : null}
            <PensionBannerWrapper />
            <PensionNovelBannerWrapper />
            <PensionDpsMigrationBannerWrapper />
        </WrapperComponent>
    );
};

type InfoBannerWrapperProps = {
    pageName: string;
};

export const InfoBannersWrapper: FC<InfoBannerWrapperProps & InfoBannersProps> = ({
    pageName,
    showCoreSystemDownWarning,
}) => {
    if (pageName !== "contracts") return null;

    return <InfoBanners showCoreSystemDownWarning={showCoreSystemDownWarning} />;
};
